import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { Heading } from '../primitives/Heading';
import { Text } from '../primitives/Text';

const Root = chakra('section', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    bg: 'surface.default',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'xl',
    overflow: 'hidden',
  },
});

const Header = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '3',
    px: '5',
    py: '4',
    borderBottomWidth: '1px',
    borderColor: 'border.subtle',
    minWidth: 0,
  },
});

const HeaderText = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '0.5', minWidth: 0 },
});

const Actions = chakra('div', {
  base: { display: 'flex', alignItems: 'center', gap: '2', flexShrink: 0 },
});

type PadToken = 'none' | '3' | '4' | '5' | '6';

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  /** Right-aligned header actions (buttons, links). */
  actions?: ReactNode;
  /** Optional leading node in the header (an icon, status badge). */
  leading?: ReactNode;
  /** Body padding (token). Default `5`; `none` for flush content like tables. */
  bodyPadding?: PadToken;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  children: ReactNode;
}

/**
 * A titled content panel — header (title + description + actions) over a body — the
 * building block for detail-view sections. Tokened surface, border-first elevation.
 */
export const Panel = forwardRef<HTMLElement, PanelProps>(function Panel(
  { title, description, actions, leading, bodyPadding = '5', headingLevel = 2, children, ...rest },
  ref,
) {
  const hasHeader = title != null || actions != null || leading != null;
  const bodyStyle: HTMLChakraProps<'div'> = { p: bodyPadding === 'none' ? '0' : bodyPadding, minWidth: 0 };
  return (
    <Root ref={ref} {...rest}>
      {hasHeader && (
        <Header>
          <chakra.div display="flex" alignItems="flex-start" gap="2.5" minW={0}>
            {leading}
            <HeaderText>
              {title != null && (
                <Heading level={headingLevel} size="sm">
                  {title}
                </Heading>
              )}
              {description != null && (
                <Text tone="secondary" size="sm">
                  {description}
                </Text>
              )}
            </HeaderText>
          </chakra.div>
          {actions != null && <Actions>{actions}</Actions>}
        </Header>
      )}
      <chakra.div {...bodyStyle}>{children}</chakra.div>
    </Root>
  );
});
